"use client"

import { Check } from "lucide-react"
import type { ShareableAgent } from "@/lib/community/publish-helpers"

interface PublishAgentPickerProps {
  agents: ShareableAgent[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  scenarioTitle: string
}

export function PublishAgentPicker({
  agents,
  selectedIds,
  onToggle,
  scenarioTitle,
}: PublishAgentPickerProps) {
  const count = selectedIds.size
  const selectedAgents = agents.filter((a) => selectedIds.has(a.id))

  return (
    <div className="space-y-2.5">
      <p className="text-caption text-foreground/70 leading-relaxed">
        {count === 0
          ? `选择你想推荐的 Agent — 来自「${scenarioTitle}」`
          : count === 1
            ? `推荐「${selectedAgents[0].name}」— ${selectedAgents[0].role}专家`
            : `已选 ${count} 个 Agent，推荐给有同样需求的用户`}
      </p>

      <div className="grid grid-cols-2 gap-1.5">
        {agents.map((agent) => {
          const isSelected = selectedIds.has(agent.id)
          return (
            <button
              key={agent.id}
              type="button"
              onClick={() => onToggle(agent.id)}
              className={`relative flex flex-col items-start rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${
                isSelected
                  ? "bg-violet-500/8 border border-violet-500/25 ring-1 ring-violet-500/10"
                  : "bg-secondary/20 border border-transparent hover:bg-secondary/40"
              }`}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-500">
                  <Check className="h-2.5 w-2.5 text-white" />
                </div>
              )}

              {/* Agent name */}
              <span className="text-xs font-semibold text-foreground truncate w-full pr-5">
                {agent.name}
              </span>

              {/* Role badge */}
              <span className="text-nano bg-violet-500/10 text-violet-500 px-1.5 py-0.5 rounded-full mt-1">
                {agent.role}
              </span>

              {/* Stats */}
              <div className="flex items-center gap-2 mt-1.5 text-micro text-muted-foreground">
                <span>{agent.skillNames.length} 技能</span>
                <span>{agent.toolCount} 工具</span>
              </div>
            </button>
          )
        })}
      </div>

      {agents.length === 0 && (
        <p className="text-caption text-muted-foreground/50 text-center py-4">
          该场景暂无 Agent 数据
        </p>
      )}
    </div>
  )
}
